import React, { useEffect, useMemo, useState } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CButton,
  CRow,
  CCol,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CFormSelect,
  CFormInput,
  CModal,
  CModalHeader,
  CModalBody,
  CModalFooter,
} from "@coreui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteProduct,
  fetchAllProducts,
  generateVideoUrl,
} from "../../features/seller/sellerSlice";
import AppLoader from "../../components/seller/AppLoader";
import { capitalizeFirst } from "../../utils/stringUtils";
import DeleteConfirmationModal from "../../components/seller/DeleteConfirmationModal";
import AppToast from "../../components/seller/AppToast";
import RejectionReasonModal from "../../components/seller/RejectionReasonModal";
import { FaEdit, FaRedo, FaTrash } from "react-icons/fa";
import CIcon from "@coreui/icons-react";
import {
  cilLoopCircular,
  cilPencil,
  cilPlus,
  cilRecycle,
  cilReload,
  cilTrash,
} from "@coreui/icons";
import {
  formatCurrency,
  formatPrice,
  formatWithCommasAndDecimals,
} from "../../utils/price";
import GenerateVideoLinkModal from "../../components/seller/GenerateVideoLinkModal";
import { useSearchParams } from "react-router-dom";

const Products = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { list: products, loading } = useSelector(
    (state) => state.seller.product,
  );
  const videoUrlState = useSelector((state) => state.seller.videoUrl);
  const [generateSuccessMsg, setGenerateSuccessMsg] = useState("");
  const [generateErrorMsg, setGenerateErrorMsg] = useState("");

  const [generateModal, setGenerateModal] = useState({
    visible: false,
    productId: null,
  });

  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteModal, setDeleteModal] = useState({
    visible: false,
    product: null,
  });

  const [toastVisible, setToastVisible] = useState(false);
  const [toastType, setToastType] = useState("info");
  const [toastMessage, setToastMessage] = useState("");
  const [toastCta, setToastCta] = useState(null); // optional CTA: { label, onClick }
  const [rejectionModal, setRejectionModal] = useState({
    visible: false,
    reason: "",
  });

  const [filters, setFilters] = useState({
    category: "",
    subcategory: "",
    location: "",
    status: "",
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  console.log("Product list:", products);

  useEffect(() => {
    dispatch(fetchAllProducts())
      .unwrap()
      .catch((error) => {
        console.error("Failed to load products", error);
        // Show toast or fallback UI
      });
  }, [dispatch]);

  // Check for toast message in navigation state
  useEffect(() => {
    // console.log("[Products DEBUG] location.state?.toast =", location.state?.toast);
    if (location.state?.toast) {
      const { type, message, ctaLabel, ctaUrl } = location.state.toast;
      setToastType(type);
      setToastMessage(message);
      // Reconstruct CTA onClick from the serialized url string
      if (ctaLabel && ctaUrl) {
        setToastCta({
          label: ctaLabel,
          onClick: () => navigate(ctaUrl),
        });
      } else {
        setToastCta(null);
      }
      setToastVisible(true);

      // Clean up the state so it doesn't show again on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // const openDeleteModal = (id) => {
  //   setDeleteModal({ visible: true, id });
  // };

  const confirmDelete = async () => {
    try {
      // console.log("Delete id:", deleteModal.product.id);
      const res = await dispatch(
        deleteProduct({ id: deleteModal.product.id, data: {} }),
      ).unwrap();

      setDeleteModal({ visible: false, id: null });
      dispatch(fetchAllProducts());

      setToastType("success");
      setToastMessage(res?.message || "Product deleted successfully.");
      setToastVisible(true);
    } catch (err) {
      console.error("Failed to delete", err);
      setToastType("error");
      setToastMessage(err?.message || "Failed to delete product.");
      setToastVisible(true);
    }
  };

  const getNestedValue = (obj, keyPath) =>
    keyPath.split(".").reduce((acc, key) => acc?.[key], obj) ?? "";

  const [searchParams] = useSearchParams();
  useEffect(() => {
    const statusFromUrl = searchParams.get("status");

    if (statusFromUrl) {
      setFilters((prev) => ({
        ...prev,
        status: statusFromUrl,
      }));
    }
  }, [searchParams]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();

    return products.filter((p) => {
      const matchesSearch = query
        ? [
          p.listing_id,
          p.name,
          p.category?.name,
          p.subcategory?.name,
          p.location,
        ]
          .filter(Boolean)
          .some((field) => field.toString().toLowerCase().includes(query))
        : true;

      const matchesCategory = filters.category
        ? p.category?.name === filters.category
        : true;

      const matchesSubcategory = filters.subcategory
        ? p.subcategory?.name === filters.subcategory
        : true;

      const matchesLocation = filters.location
        ? p.location === filters.location
        : true;

      const matchesStatus = filters.status ? p.status === filters.status : true;

      return (
        matchesSearch &&
        matchesCategory &&
        matchesSubcategory &&
        matchesLocation &&
        matchesStatus
      );
    });
  }, [products, search, filters]);

  const sorted = useMemo(() => {
    if (!sortConfig.key) return filtered;
    const sortedData = [...filtered].sort((a, b) => {
      const aVal = a[sortConfig.key]?.toString().toLowerCase() ?? "";
      const bVal = b[sortConfig.key]?.toString().toLowerCase() ?? "";
      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sortedData;
  }, [filtered, sortConfig]);

  const indexOfLast = currentPage * entriesPerPage;
  const indexOfFirst = indexOfLast - entriesPerPage;
  const currentData = sorted.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(sorted.length / entriesPerPage);
  // console.log('Reject Reason Api Data:', currentData);
  // console.log('Reject Reason Modal:', rejectionModal);

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const categories = useMemo(() => {
    return [...new Set(products.map((p) => p.category?.name).filter(Boolean))];
  }, [products]);

  const subcategories = useMemo(() => {
    return [
      ...new Set(
        products
          .filter(
            (p) => !filters.category || p.category?.name === filters.category,
          )
          .map((p) => p.subcategory?.name)
          .filter(Boolean),
      ),
    ];
  }, [products, filters.category]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <AppLoader />;
  }

  const locations = [
    ...new Set(products.map((p) => p.location).filter(Boolean)),
  ];
  const statuses = ["approved", "rejected", "pending", "draft"];

  const handleConfirmGenerate = async () => {
    try {
      const id = generateModal.productId;

      const res = await dispatch(generateVideoUrl(id)).unwrap();

      setGenerateSuccessMsg(
        "Video Verification Link has been sent to your registered email and phone number.",
      );

      // Refresh products so button hides immediately
      // dispatch(fetchAllProducts());
    } catch (err) {
      setGenerateSuccessMsg(""); // clear success
      setGenerateErrorMsg(err?.message || "Failed to generate video verification link.");
    }
  };

  const handleCloseGenerateModal = () => {
    setGenerateModal({ visible: false, productId: null });
    setGenerateSuccessMsg("");

    // Refresh when modal finally closes
    dispatch(fetchAllProducts());
  };

  const isCooldownActive = (expiresAt) => {
    if (!expiresAt) return false;
    return Date.now() < expiresAt;
  };

  const goToDetail = (id) => {
    navigate(`/seller/product-detail/${id}`);
  };

  const getVideoStatus = (product) => {
    const hasVideoUrl = !!product.video_url;

    const cooldown = videoUrlState?.cooldowns?.[product.id];
    const isLinkActive = cooldown && Date.now() < cooldown.expires_at;

    if (hasVideoUrl) return "uploaded";
    if (isLinkActive) return "sent";
    return "not_uploaded";
  };

  return (
    <>
      <div className="container-fluid px-3">
        <CRow>
          <CCol xs={12} md={6}>
            <h4 className="mb-3">Create Bid Listings</h4>
          </CCol>
          <CCol xs="12" className="d-flex justify-content-end gap-2">
            <CButton
              color="primary"
              variant="outline"
              onClick={() => navigate("/seller/add-product")}
            >
              <CIcon icon={cilPlus} className="me-2" />
              Create Bid
            </CButton>

            <CButton
              color="secondary"
              variant="outline"
              onClick={() => navigate("/seller/view-trash")}
            >
              <CIcon icon={cilRecycle} style={{ fontSize: "1.2rem" }} />
            </CButton>
          </CCol>
        </CRow>

        <CCard className="mt-3">
          <CCardHeader>
            <CRow className="align-items-center g-2">
              {/* Filters Section */}
              <CCol xs={12} md={3} className="align-items-center">
                <CFormSelect
                  size="sm"
                  value={filters.category}
                  onChange={(e) => {
                    const newCategory = e.target.value;
                    setFilters({
                      category: newCategory,
                      subcategory: "",
                      location: "",
                      status: "",
                    });
                  }}
                >
                  <option value="">Categories</option>
                  {categories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>
              <CCol xs={12} md={3}>
                <CFormSelect
                  size="sm"
                  value={filters.subcategory}
                  onChange={(e) =>
                    handleFilterChange("subcategory", e.target.value)
                  }
                >
                  <option value="">Subcategories</option>
                  {subcategories.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>
              <CCol xs={12} md={3}>
                <CFormSelect
                  size="sm"
                  value={filters.location}
                  onChange={(e) =>
                    handleFilterChange("location", e.target.value)
                  }
                >
                  <option value="">Locations</option>
                  {locations.map((l) => (
                    <option key={l} value={l}>
                      {l}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>

              <CCol xs={12} md={2}>
                <CFormSelect
                  size="sm"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  <option value="">Statuses</option>
                  {statuses.map((s) => (
                    <option key={s} value={s}>
                      {capitalizeFirst(s)}
                    </option>
                  ))}
                </CFormSelect>
              </CCol>
              <CCol xs={12} md={1} className="text-end">
                <CButton
                  color="secondary"
                  className="d-flex align-items-center justify-content-center rounded"
                  style={{
                    width: "35px",
                    height: "35px",
                    padding: 0,
                    background: "rgba(108,117,125,0.15)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(108,117,125,0.4)",
                    color: "#6c757d",
                    boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                    transition: "all 0.25s ease",
                  }}
                  title="Reset Filters"
                  onClick={() =>
                    setFilters({
                      category: "",
                      subcategory: "",
                      location: "",
                      status: "",
                    })
                  }
                >
                  <CIcon icon={cilReload} style={{ fontSize: "1.1rem" }} />
                </CButton>
              </CCol>
            </CRow>
            <CRow className="align-items-center flex-column flex-md-row mt-2">
              <CCol xs={12} md={6} className="mb-2 mb-md-0">
                Show{" "}
                <CFormSelect
                  size="sm"
                  style={{ display: "inline-block", width: "auto" }}
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                  options={[
                    { label: "5", value: "5" },
                    { label: "10", value: "10" },
                    { label: "25", value: "25" },
                  ]}
                />{" "}
                entries
              </CCol>

              <CCol xs={12} md={6} className="text-end">
                Search:{" "}
                <CFormInput
                  type="text"
                  size="sm"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ display: "inline-block", width: "200px" }}
                />
              </CCol>
            </CRow>
          </CCardHeader>

          <CCardBody>
            <CTable responsive bordered hover>
              <CTableHead color="light">
                <CTableRow>
                  {[
                    { key: "index", label: "#" },
                    { key: "listing_id", label: "Listing Id" },
                    { key: "image_url", label: "Image" },
                    { key: "name", label: "Name" },
                    { key: "category.name", label: "Category" },
                    { key: "subcategory.name", label: "Subcategory" },
                    // { key: "stock.quantity", label: "Stock" }, // optional if stock object exists
                    { key: "quantity", label: "Quantity" },
                    { key: "unit_of_measurement", label: "Unit" },
                    { key: "price", label: "Price" },
                    { key: "location", label: "Location" },
                    { key: "status", label: "Listing Status" },
                    { key: "rejection_reason", label: "Rejection Reason" },
                    { key: "video_status", label: "Video Status" },
                  ].map((col) => (
                    <CTableHeaderCell
                      key={col.key}
                      onClick={() =>
                        col.key !== "image_url" &&
                        col.key !== "rejection_reason" &&
                        col.key !== "video_status" &&
                        col.key !== "index" &&
                        handleSort(col.key)
                      }
                      style={{
                        cursor:
                          col.key !== "image_url" &&
                            col.key !== "rejection_reason" &&
                            col.key !== "video_status" &&
                            col.key !== "index"
                            ? "pointer"
                            : "default",
                      }}
                    >
                      {col.label}{" "}
                      {sortConfig.key === col.key
                        ? sortConfig.direction === "asc"
                          ? "▲"
                          : "▼"
                        : ""}
                    </CTableHeaderCell>
                  ))}
                  <CTableHeaderCell>Actions</CTableHeaderCell>
                </CTableRow>
              </CTableHead>
              <CTableBody>
                {currentData.map((product, index) => (
                  <CTableRow
                    key={product.id}
                    onClick={() => goToDetail(product.id)}
                    style={{ cursor: "pointer" }}
                  >
                    <CTableDataCell>{indexOfFirst + index + 1}</CTableDataCell>
                    <CTableDataCell>{product.listing_id || "-"}</CTableDataCell>
                    <CTableDataCell>
                      {product.image_url ? (
                        <img
                          src={product.image_url}
                          alt={product.name}
                          loading="lazy"
                          className="img-fluid"
                          style={{
                            maxWidth: 80,
                            height: "auto",
                            objectFit: "contain",
                          }}
                        />
                      ) : (
                        "-"
                      )}
                    </CTableDataCell>
                    <CTableDataCell>{product.name || "-"}</CTableDataCell>
                    <CTableDataCell>
                      {product.category?.name || "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {product.subcategory?.name || "-"}
                    </CTableDataCell>
                    {/* <CTableDataCell>
                      {product.stock?.quantity ?? "-"}
                    </CTableDataCell> */}
                    <CTableDataCell>{product.quantity ?? "-"}</CTableDataCell>
                    <CTableDataCell>
                      {product.unit_of_measurement || "-"}
                    </CTableDataCell>
                    <CTableDataCell>
                      {formatCurrency(product.price, product.currency)}
                      {/* {" "}
                      {product.price
                        ? `A$ ${formatWithCommasAndDecimals(formatPrice(product.price))}`
                        : "-"} */}
                    </CTableDataCell>

                    <CTableDataCell>{product.location || "-"}</CTableDataCell>
                    <CTableDataCell>
                      <span
                        className={`badge bg-${product.status === "approved"
                          ? "success"
                          : product.status === "rejected"
                            ? "danger"
                            : product.status === "draft"
                              ? "secondary"
                              : "warning"
                          }`}
                      >
                        {capitalizeFirst(product.status || "-")}
                      </span>
                    </CTableDataCell>
                    <CTableDataCell>
                      {product.rejection_reason ? (
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            setRejectionModal({
                              visible: true,
                              reason: product.rejection_reason,
                            });
                          }}
                          style={{
                            color: "#dc3545",
                            textDecoration: "underline",
                            cursor: "pointer",
                            fontWeight: 500,
                          }}
                          title="Click to view full reason"
                        >
                          View Reason
                        </span>
                      ) : (
                        "-"
                      )}
                    </CTableDataCell>
                    <CTableDataCell>
                      {(() => {
                        const status = getVideoStatus(product);

                        const badgeMap = {
                          sent: "info",
                          uploaded: "success",
                          not_uploaded: "danger",
                        };

                        const labelMap = {
                          sent: "Sent",
                          uploaded: "Uploaded",
                          not_uploaded: "Not Uploaded",
                        };

                        return (
                          <span className={`badge bg-${badgeMap[status]}`}>
                            {labelMap[status]}
                          </span>
                        );
                      })()}
                    </CTableDataCell>

                    <CTableDataCell>
                      <div className="d-flex flex-column flex-md-row gap-2">
                        {/* EDIT BUTTON */}
                        {product.status !== "approved" && (
                          <CButton
                            color="info"
                            className="d-flex align-items-center justify-content-center rounded"
                            style={{
                              width: "30px",
                              height: "30px",
                              padding: 0,
                              background: "rgba(13, 202, 240, 0.15)",
                              backdropFilter: "blur(8px)",
                              border: "1px solid rgba(13, 202, 240, 0.4)",
                              boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
                              color: "#0dcaf0",
                              transition: "all 0.25s ease",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/seller/add-product/${product.id}`);
                            }}
                            title="Edit"
                          >
                            <CIcon
                              icon={cilPencil}
                              style={{ fontSize: "1.1rem" }}
                            />
                          </CButton>
                        )}

                        {/* DELETE BUTTON */}
                        <CButton
                          color="danger"
                          className="d-flex align-items-center justify-content-center rounded"
                          style={{
                            width: "30px",
                            height: "30px",
                            padding: 0,
                            background: "rgba(220, 53, 69, 0.15)",
                            backdropFilter: "blur(8px)",
                            border: "1px solid rgba(220, 53, 69, 0.4)",
                            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
                            color: "#dc3545",
                            transition: "all 0.25s ease",
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteModal({ visible: true, product });
                          }}
                          title="Delete"
                        >
                          <CIcon
                            icon={cilTrash}
                            style={{ fontSize: "1.1rem" }}
                          />
                        </CButton>

                        {/* REGENERATE LINK BUTTON */}
                        {(() => {
                          // const noVideoUrl =
                          //   !product.video_url || product.video_url === "";

                          // const productCooldown =
                          //   videoUrlState?.cooldowns?.[product.id];
                          // const cooldownActive = productCooldown
                          //   ? isCooldownActive(productCooldown.expires_at)
                          //   : false;

                          // const showGenerate =
                          //   product.status !== "approved" &&
                          //   noVideoUrl &&
                          //   !cooldownActive;

                          const videoStatus = getVideoStatus(product);

                          const showGenerate =
                            product.status !== "approved" &&
                            videoStatus === "not_uploaded";

                          if (!showGenerate) return null;
                          return (
                            <CButton
                              color="success"
                              className="d-flex align-items-center justify-content-center rounded"
                              style={{
                                width: "30px",
                                height: "30px",
                                padding: 0,
                                background: "rgba(25, 135, 84, 0.15)",
                                backdropFilter: "blur(8px)",
                                border: "1px solid rgba(25, 135, 84, 0.4)",
                                boxShadow: "0 2px 6px rgba(0, 0, 0, 0.25)",
                                color: "#198754",
                                transition: "all 0.25s ease",
                              }}
                              title="Regenerate Video Verification Link"
                              onClick={(e) => {
                                e.stopPropagation();
                                setGenerateModal({
                                  visible: true,
                                  productId: product.id,
                                });
                              }}
                            >
                              <CIcon
                                icon={cilLoopCircular}
                                style={{ fontSize: "1.0rem" }}
                              />
                            </CButton>
                          );
                        })()}
                      </div>
                    </CTableDataCell>
                  </CTableRow>
                ))}
                {currentData.length === 0 && (
                  <CTableRow>
                    <CTableDataCell
                      colSpan="14"
                      className="text-center text-muted"
                    >
                      No Products found.
                    </CTableDataCell>
                  </CTableRow>
                )}
              </CTableBody>
            </CTable>

            <div className="d-flex justify-content-between align-items-center mt-3 flex-column flex-md-row gap-2">
              <div>
                Showing {indexOfFirst + 1} to{" "}
                {Math.min(indexOfLast, filtered.length)} of {filtered.length}{" "}
                entries
              </div>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="me-2"
                >
                  Prev
                </CButton>

                {[...Array(totalPages)].map((_, i) => (
                  <CButton
                    key={i}
                    size="sm"
                    color={currentPage === i + 1 ? "primary" : "light"}
                    onClick={() => setCurrentPage(i + 1)}
                    className="me-2 mb-1"
                  >
                    {i + 1}
                  </CButton>
                ))}

                <CButton
                  size="sm"
                  color="light"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </CButton>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </div>
      <DeleteConfirmationModal
        visible={deleteModal.visible}
        itemName={deleteModal.product?.name}
        onClose={() => setDeleteModal({ visible: false, product: null })}
        onConfirm={confirmDelete}
      />
      <AppToast
        visible={toastVisible}
        setVisible={setToastVisible}
        type={toastType}
        message={toastMessage}
        cta={toastCta}
      />
      <RejectionReasonModal
        visible={rejectionModal.visible}
        reason={rejectionModal.reason}
        onClose={() => setRejectionModal({ visible: false, reason: "" })}
      />
      <GenerateVideoLinkModal
        visible={generateModal.visible}
        onClose={handleCloseGenerateModal}
        onGenerate={handleConfirmGenerate}
        loading={videoUrlState.loading}
        successMessage={generateSuccessMsg}
        errorMessage={generateErrorMsg}
      />
    </>
  );
};

export default Products;
